/* QH Management — Screen components */

const { useState, useMemo, useEffect, useRef } = React;

// ============== LOGIN ==============
// Two-phase: credentials → OTP code from email → tokens. The server may
// signal otpRequired=false in the password-step response when SMTP isn't
// configured (or the user's stored email isn't a deliverable address) —
// in that case we skip straight to onLogin() so the very first admin
// sign-in works out of the box.
function LoginScreen({ onLogin, palette, logoVariant }) {
  const [stage, setStage] = useState('credentials'); // 'credentials' | 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // OTP-stage state
  const [challengeId, setChallengeId] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [code, setCode] = useState('');
  const [otpHint, setOtpHint] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  // Resend button cooldown: the user has to wait 60s after the most
  // recent send (initial or via Resend) before they can request another.
  // Server-side rate-limiting is a separate backstop ("login" policy on
  // the endpoint), but the cooldown spares the user from clicking and
  // getting a 429.
  const RESEND_COOLDOWN_SECONDS = 60;
  const [resendAvailableAt, setResendAvailableAt] = useState(null);
  const [tick, setTick] = useState(0);

  // Live countdown to the OTP expiry + resend cooldown. One interval
  // drives both displays via the `tick` re-render.
  useEffect(() => {
    if (stage !== 'otp') return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [stage]);
  const secondsLeft = expiresAt ? Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)) : 0;
  const resendSecondsLeft = resendAvailableAt
    ? Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000))
    : 0;
  const canResend = resendSecondsLeft === 0;

  const submitCredentials = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Both fields are required.'); return; }
    setError(''); setBusy(true);
    try {
      const r = await window.QH_API.post('/auth/login', { email, password });
      if (r.otpRequired === false) {
        // SMTP/email-bootstrap fallback — tokens issued directly.
        window.QH_API.setToken(r.accessToken);
        await window.QH_STORE.loadAll();
        onLogin();
        return;
      }
      // OTP required → switch stages.
      setChallengeId(r.challengeId);
      setMaskedEmail(r.maskedEmail || '');
      setExpiresAt(Date.now() + (r.expiresInSeconds || 300) * 1000);
      setResendAvailableAt(Date.now() + RESEND_COOLDOWN_SECONDS * 1000);
      setCode('');
      setOtpHint(`A 6-digit code is on its way to ${r.maskedEmail || 'your email'}.`);
      setStage('otp');
    } catch (err) {
      setError(err.message || 'Login failed.');
    } finally { setBusy(false); }
  };

  // Auto-submit fires from onChange the moment the input reaches 6 digits.
  // We track the last auto-attempted code in a ref so a failed attempt
  // doesn't re-fire on every keystroke as the user edits — but if they
  // delete back to 5 and re-type a different value to 6, it fires again.
  const lastAutoCode = useRef('');
  const verifyOtp = async (codeToVerify) => {
    const v = (codeToVerify ?? '').trim();
    if (!/^\d{6}$/.test(v)) { setError('Enter the 6-digit code.'); return; }
    setError(''); setBusy(true);
    try {
      const r = await window.QH_API.post('/auth/login/verify-otp', { challengeId, code: v });
      window.QH_API.setToken(r.accessToken);
      await window.QH_STORE.loadAll();
      onLogin();
    } catch (err) {
      setError(err.message || 'Verification failed.');
    } finally { setBusy(false); }
  };
  const submitOtp = (e) => { e.preventDefault(); verifyOtp(code); };
  const onCodeChange = (raw) => {
    const v = raw.replace(/\D+/g, '').slice(0, 6);
    setCode(v);
    // Fire once per distinct 6-digit value — and only when the form is idle,
    // so we don't stack requests while a previous verify is in flight.
    if (v.length === 6 && !busy && v !== lastAutoCode.current) {
      lastAutoCode.current = v;
      verifyOtp(v);
    }
  };

  const resendOtp = async () => {
    if (!canResend) return;     // belt-and-braces: the button is disabled too
    setError(''); setBusy(true);
    try {
      const r = await window.QH_API.post('/auth/login/resend-otp', { challengeId, code: '' });
      setExpiresAt(Date.now() + (r.expiresInSeconds || 300) * 1000);
      setResendAvailableAt(Date.now() + RESEND_COOLDOWN_SECONDS * 1000);
      setOtpHint('A new code is on its way — check your inbox.');
      // Old code is now invalid server-side; clear the auto-submit guard
      // so the user can type the new one and have it auto-verify.
      setCode(''); lastAutoCode.current = '';
    } catch (err) {
      setError(err.message || 'Could not resend code.');
    } finally { setBusy(false); }
  };

  const goBackToCredentials = () => {
    setStage('credentials'); setError(''); setCode(''); setChallengeId(null);
    lastAutoCode.current = '';
  };

  return (
    <div className="login-bg">
      <div className="login-art">
        <div className="login-art-grid"></div>
        <div style={{ position: 'relative' }}>
          <QHLogo variant={logoVariant} palette={palette} size={36} />
        </div>
        <div style={{ position: 'relative', maxWidth: 460 }}>
          <div style={{ fontSize: 32, fontWeight: 600, lineHeight: 1.15, letterSpacing: '-0.02em', marginBottom: 14, color: 'var(--ink)' }}>
            Run schemes & finance with auditable precision.
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--ink-3)' }}>
            Maturity-aware deposits, RBAC down to the page · action level, and traceable cheque cycles — built for trust-driven finance teams.
          </div>
          <div style={{ display: 'flex', gap: 24, marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--line)' }}>
            <div><div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 600 }}></div><div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 2 }}></div></div>
            <div><div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 600 }}></div><div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 2 }}></div></div>
            <div><div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 600 }}></div><div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 2 }}></div></div>
          </div>
        </div>
        <div style={{ position: 'relative', fontSize: 11, opacity: 0.5, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {'\n'}
        </div>
      </div>

      <div className="login-form-wrap">
        {stage === 'credentials' ? (
          <form className="login-form" onSubmit={submitCredentials}>
            <div style={{ display: 'none' }}>
              <QHLogo variant={logoVariant} palette={palette} size={32} />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>Welcome back</div>
              <div className="muted text-sm" style={{ marginTop: 4 }}>Sign in to access your finance workspace.</div>
            </div>
            <div className="field">
              <label>Username or email</label>
              <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin or you@qh.bh" autoFocus />
            </div>
            <div className="field">
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <span>Password</span>
                <a style={{ marginLeft: 'auto', textTransform: 'none', letterSpacing: 0, fontSize: 11, color: 'var(--accent-ink)', cursor: 'pointer' }}>Forgot?</a>
              </label>
              <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" />
            </div>
            {error && <div className="err" style={{ color: 'var(--neg)', fontSize: 12 }}>{error}</div>}
            <button className="btn primary lg" type="submit" disabled={busy} style={{ justifyContent: 'center' }}>{busy ? 'Signing in…' : 'Continue'}<I.ArrowRight /></button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ink-3)', fontSize: 11 }}>
              <I.Lock s={12} /> TLS 1.3 · two-factor authentication
            </div>
          </form>
        ) : (
          <form className="login-form" onSubmit={submitOtp}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>Verify it's you</div>
              <div className="muted text-sm" style={{ marginTop: 4, lineHeight: 1.5 }}>{otpHint}</div>
            </div>
            <div className="field">
              <label>6-digit code</label>
              <input className="input font-mono" autoFocus inputMode="numeric" pattern="[0-9]*" maxLength={6}
                     value={code} onChange={(e) => onCodeChange(e.target.value)}
                     placeholder="000000"
                     style={{ fontSize: 22, letterSpacing: 6, textAlign: 'center', fontWeight: 600 }} />
              <div className="text-xs muted" style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                {secondsLeft > 0
                  ? <>Code expires in <b className="font-mono">{Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}</b></>
                  : <span style={{ color: 'var(--neg)' }}>Code expired — request a new one.</span>}
                <span style={{ flex: 1 }} />
                <button type="button" className="btn ghost sm"
                        onClick={resendOtp}
                        disabled={busy || !canResend}
                        title={canResend ? 'Send a fresh code' : `Wait ${resendSecondsLeft}s before requesting another code`}>
                  {canResend ? 'Resend code' : `Resend in ${resendSecondsLeft}s`}
                </button>
              </div>
            </div>
            {error && <div className="err" style={{ color: 'var(--neg)', fontSize: 12 }}>{error}</div>}
            <button className="btn primary lg" type="submit" disabled={busy || code.length !== 6} style={{ justifyContent: 'center' }}>{busy ? 'Verifying…' : 'Sign in'}<I.ArrowRight /></button>
            <button type="button" className="btn ghost" onClick={goBackToCredentials} disabled={busy} style={{ justifyContent: 'center' }}>
              <I.ChevronRight s={11} style={{ transform: 'rotate(180deg)' }} /> Back to sign-in
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ink-3)', fontSize: 11 }}>
              <I.Lock s={12} /> Code is single-use and expires in 5 minutes.
            </div>
          </form>
        )}
      </div>
    </div>);

}

// ============== KPI strip ==============
function KPIStrip({ items }) {
  return (
    <div className="card" style={{ display: 'flex', overflow: 'hidden', marginBottom: 12 }}>
      {items.map((k, i) =>
      <div key={i} className="kpi" style={{ flex: 1 }}>
          <div className="kpi-label">{k.label}</div>
          <div className="kpi-value">{k.value}</div>
          {k.delta && <div className={`kpi-delta ${k.deltaPositive ? 'pos' : 'neg'}`}>
            {k.deltaPositive ? <I.ArrowUp /> : <I.ArrowDown />}{k.delta}
          </div>}
          {k.sub && <div className="text-xs muted">{k.sub}</div>}
        </div>
      )}
    </div>);

}

// ============== DASHBOARD ==============
function Dashboard({ go, role }) {
  const totalDeposits = QH_DATA.schemes.reduce((s, x) => s + x.totalAmount, 0);
  const matured = QH_DATA.schemes.reduce((s, x) => s + (x.matured || 0), 0);
  const nonMatured = QH_DATA.schemes.reduce((s, x) => s + (x.nonMatured || 0), 0);
  const approvedFin = QH_DATA.financeApps.filter((a) => a.status === 'approved').reduce((s, x) => s + x.amount, 0);
  const pendingApps = QH_DATA.financeApps.filter((a) => a.status === 'pending').length;
  const overdueRepay = QH_DATA.repayments.filter((r) => r.status === 'overdue' || r.status === 'bounced').length;

  return (
    <div className="page-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <h1 style={{margin:0,fontSize:24}}>Dashboard</h1>
        <div style={{flex:1}}/>
        <button className="btn" onClick={() => window.QH_STORE.reload()} title="Reload data"><I.Refresh/></button>
      </div>
      <KPIStrip items={[
      { label: 'Total under management', value: fmtBHD(totalDeposits) + ' BHD', delta: '+8.2% MoM', deltaPositive: true },
      { label: 'Matured · payable', value: fmtBHD(matured) + ' BHD', sub: 'across 3 schemes' },
      { label: 'Non-matured', value: fmtBHD(nonMatured) + ' BHD', sub: '555 deposits tracked' },
      { label: 'Approved finance', value: fmtBHD(approvedFin) + ' BHD', sub: `${pendingApps} pending review` },
      { label: 'Defaulters', value: QH_DATA.defaulters.length, sub: `${overdueRepay} repayments at risk` }]
      } />

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <div className="card">
          <div className="card-header">
            Cash flow · Last 6 months
            <div className="actions"><span className="tag pos"><I.TrendingUp s={10} /> Up 86% YoY</span></div>
          </div>
          <div className="card-body" style={{ padding: 16 }}>
            <CashFlowChart data={QH_DATA.monthlyDeposits} />
          </div>
        </div>

        <div className="card">
          <div className="card-header">Pending actions <span className="tag accent" style={{ marginLeft: 'auto' }}>{pendingApps + overdueRepay}</span></div>
          <div className="card-body" style={{ padding: 0 }}>
            {[
            { icon: <I.Hand s={14} />, title: '1 finance application awaiting approval', sub: 'Khalid Al Doseri · 12,000.000 BHD · business', go: 'finance' },
            { icon: <I.AlertCircle s={14} />, title: '1 cheque bounced — defaulter flagged', sub: 'Reem Al Doseri · CHQ-330114', go: 'repayments' },
            { icon: <I.AlertCircle s={14} />, title: '1 repayment overdue', sub: 'Aisha Al Khalifa · CHQ-001882 · 30 Apr', go: 'repayments' },
            { icon: <I.Calendar s={14} />, title: '12 maturities due this month', sub: 'Riyadh Heritage Pool · 8,400.000 BHD', go: 'schemes' }].
            map((a, i) =>
            <button key={i} className="nav-item" onClick={() => go(a.go)} style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)', height: 'auto', alignItems: 'flex-start' }}>
                <span style={{ color: 'var(--accent-ink)', background: 'var(--accent-soft)', width: 28, height: 28, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{a.icon}</span>
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                  <span style={{ fontSize: 12, fontWeight: 500 }}>{a.title}</span>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{a.sub}</span>
                </span>
                <I.ChevronRight s={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12 }}>
        <div className="card">
          <div className="card-header">Active schemes <span className="actions"><button className="btn sm ghost" onClick={() => go('schemes')}>View all<I.ArrowRight s={11} /></button></span></div>
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table className="data">
              <thead><tr><th>Scheme</th><th className="right">Members</th><th className="right">Balance · BHD</th><th className="right">Matured</th><th></th></tr></thead>
              <tbody>
                {QH_DATA.schemes.filter((s) => s.status === 'active').map((s) =>
                <tr key={s.id} className="clickable" onClick={() => go('scheme:' + s.id)}>
                    <td><div style={{ fontWeight: 500 }}>{s.name}</div><div className="text-xs muted font-mono">{s.code}</div></td>
                    <td className="num right">{s.members}</td>
                    <td className="num right" style={{ fontWeight: 600 }}>{fmtBHD(s.totalAmount)}</td>
                    <td className="num right pos">{fmtBHD(s.matured)}</td>
                    <td><I.ChevronRight s={12} /></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Recent activity</div>
          <div className="card-body" style={{ paddingTop: 6 }}>
            <div className="timeline">
              {QH_DATA.auditLog.slice(0, 6).map((a, i) =>
              <div key={i} className="timeline-item">
                  <div className="timeline-icon"><ActIcon action={a.action} /></div>
                  <div>
                    <div style={{ fontSize: 12 }}><b>{a.user}</b> <span className="muted">· {a.action}</span></div>
                    <div className="text-xs muted">{a.detail}</div>
                    <div className="text-xs muted-2 font-mono" style={{ marginTop: 2 }}>{window.fmtDateTime(a.ts)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>);

}

function ActIcon({ action }) {
  if (action.startsWith('login')) return <I.Lock s={12} />;
  if (action.startsWith('transaction')) return <I.Banknote s={12} />;
  if (action.startsWith('scheme')) return <I.Vault s={12} />;
  if (action.startsWith('finance')) return <I.Hand s={12} />;
  if (action.startsWith('user') || action.startsWith('role')) return <I.Users s={12} />;
  if (action.startsWith('notification')) return <I.MessageSquare s={12} />;
  return <I.Eye s={12} />;
}

function CashFlowChart({ data }) {
  const max = Math.max(...data.map((d) => d.dep));
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, height: 180, padding: '10px 0' }}>
        {data.map((d, i) =>
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 3, width: '100%', justifyContent: 'center' }}>
              <div style={{ width: '40%', height: `${d.dep / max * 100}%`, background: 'var(--brand)', borderRadius: '3px 3px 0 0', position: 'relative' }}>
                <div style={{ position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)', fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtBHDShort(d.dep)}</div>
              </div>
              <div style={{ width: '40%', height: `${d.wd / max * 100}%`, background: 'var(--accent)', opacity: 0.7, borderRadius: '3px 3px 0 0' }} />
            </div>
            <div className="text-xs muted">{d.m}</div>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--ink-3)', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, background: 'var(--brand)', borderRadius: 2 }} />Deposits</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, background: 'var(--accent)', opacity: 0.7, borderRadius: 2 }} />Withdrawals</span>
      </div>
    </div>);

}

window.LoginScreen = LoginScreen;
window.Dashboard = Dashboard;
window.KPIStrip = KPIStrip;
window.ActIcon = ActIcon;